/**
 * Real tower heights for the 3D site plan when a stack has no unit on some
 * floors — e.g. Thomson Reserve stack 30 starts at level 3, so a tower built
 * from its units alone stands two floors shorter than stack 31 beside it.
 *
 * A block's "ladder" is every floor number that has a unit in any of its
 * stacks. Each stack takes the ladder from the bottom up to its own top floor,
 * leaving `null` where it has no unit, so neighbouring stacks line up floor
 * for floor. Floor numbers no stack uses are never invented.
 */

/** Distinct floors across a block's stacks, lowest first. */
export function blockFloorLadder(floorLists: number[][]): number[] {
  const floors = new Set<number>();
  floorLists.forEach((list) => list.forEach((f) => { if (Number.isFinite(f)) floors.add(f); }));
  return Array.from(floors).sort((a, b) => a - b);
}

/**
 * One slot per ladder floor up to this stack's top floor, ground first:
 * the stack's entry for that floor, or null when it has none.
 */
export function padStoreys<T>(entries: T[], floorOf: (entry: T) => number, ladder: number[]): Array<T | null> {
  if (entries.length === 0) return [];
  const byFloor = new Map<number, T>();
  entries.forEach((e) => { const f = floorOf(e); if (!byFloor.has(f)) byFloor.set(f, e); });
  const own = Array.from(byFloor.keys());
  const top = Math.max(...own);
  const floors = blockFloorLadder([ladder, own]).filter((f) => f <= top);
  return floors.map((f) => byFloor.get(f) ?? null);
}

/** Colour for a floor where the stack has no unit (void deck, lobby, sky terrace). */
export const EMPTY_STOREY_COLOR = '#4a5361';
